const epsilon = 1e-10;
const cross = (a, b, c) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
const same = (a, b) => a.x === b.x && a.y === b.y;
const open = (ring) => ring.length > 1 && same(ring[0], ring.at(-1)) ? ring.slice(0, -1) : ring;
const on = (p, a, b) => Math.abs(cross(a, b, p)) <= epsilon && p.x >= Math.min(a.x, b.x) - epsilon && p.x <= Math.max(a.x, b.x) + epsilon && p.y >= Math.min(a.y, b.y) - epsilon && p.y <= Math.max(a.y, b.y) + epsilon;
function intersects(a, b, c, d) {
    const u = cross(a, b, c), v = cross(a, b, d), w = cross(c, d, a), x = cross(c, d, b);
    return (u * v < 0 && w * x < 0) || on(c, a, b) || on(d, a, b) || on(a, c, d) || on(b, c, d);
}
export function ringArea(ring) {
    return Math.abs(ring.reduce((n, p, i) => { const q = ring[(i + 1) % ring.length]; return n + p.x * q.y - q.x * p.y; }, 0)) / 2;
}
export function ringContains(ring, p) {
    let yes = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const a = ring[i], b = ring[j];
        if (on(p, a, b))
            return false;
        if ((a.y > p.y) !== (b.y > p.y) && p.x < (b.x - a.x) * (p.y - a.y) / (b.y - a.y) + a.x)
            yes = !yes;
    }
    return yes;
}
export function ringsOverlap(left, right) {
    const a = open(left), b = open(right);
    if (!a.length || !b.length)
        return false;
    return a.some((p, i) => b.some((q, j) => intersects(p, a[(i + 1) % a.length], q, b[(j + 1) % b.length]))) || ringContains(a, b[0]) || ringContains(b, a[0]);
}
function simple(raw) {
    const ring = open(raw);
    if (ring.length < 3 || ring.some(p => !Number.isFinite(p.x) || !Number.isFinite(p.y)) || ringArea(ring) <= epsilon)
        return false;
    for (let i = 0; i < ring.length; i++) {
        if (same(ring[i], ring[(i + 1) % ring.length]))
            return false;
        for (let j = i + 1; j < ring.length; j++) {
            if (j === i + 1 || (i === 0 && j === ring.length - 1))
                continue;
            if (intersects(ring[i], ring[(i + 1) % ring.length], ring[j], ring[(j + 1) % ring.length]))
                return false;
        }
    }
    return true;
}
/** Closing vertices and winding are retained; invalid rings are rejected, never clipped. */
export function roomRingsValid(shell, holes) {
    if (!simple(shell))
        return false;
    const edge = open(shell);
    return holes.every((hole, i) => simple(hole) && open(hole).every(p => ringContains(edge, p)) &&
        !edge.some((p, j) => open(hole).some((q, k) => intersects(p, edge[(j + 1) % edge.length], q, open(hole)[(k + 1) % open(hole).length]))) &&
        holes.slice(0, i).every(other => !ringsOverlap(hole, other)));
}
export function roomContainsPoint(shell, holes, point) {
    return ringContains(shell, point) && !holes.some(hole => ringContains(hole, point) || open(hole).some((p, i) => on(point, p, open(hole)[(i + 1) % open(hole).length])));
}
/** SVG callers must use evenodd for both fill and clipping. */
export function roomSvgPath(shell, holes = []) {
    return [shell, ...holes].map(ring => ring.length ? `M ${ring.map(p => `${p.x},${p.y}`).join(' L ')} Z` : '').join(' ');
}
/** A display anchor and an inscribed clearance radius; never alters source rings or scale. */
export function roomLabelAnchor(shell, holes = []) {
    if (!roomRingsValid(shell, holes))
        return null;
    const rings = [open(shell), ...holes.map(open)];
    const xs = shell.map(p => p.x), ys = shell.map(p => p.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    let best = null;
    function consider(point) {
        if (!roomContainsPoint(shell, holes, point))
            return;
        let distance = Infinity;
        for (const ring of rings)
            for (let i = 0; i < ring.length; i++) {
                const a = ring[i], b = ring[(i + 1) % ring.length], dx = b.x - a.x, dy = b.y - a.y;
                const t = Math.max(0, Math.min(1, ((point.x - a.x) * dx + (point.y - a.y) * dy) / (dx * dx + dy * dy)));
                distance = Math.min(distance, Math.hypot(point.x - a.x - t * dx, point.y - a.y - t * dy));
            }
        if (distance > 0 && (!best || distance > best.clearance))
            best = { point, clearance: distance };
    }
    // Horizontal slab seeds find an interior even for narrow polygons missed by the grid.
    const levels = [...new Set(rings.flatMap(r => r.map(p => p.y)))].sort((a, b) => a - b);
    for (let j = 1; j < levels.length; j++) {
        const y = (levels[j - 1] + levels[j]) / 2, cuts = [];
        for (const ring of rings)
            for (let i = 0; i < ring.length; i++) {
                const a = ring[i], b = ring[(i + 1) % ring.length];
                if ((a.y > y) !== (b.y > y))
                    cuts.push(a.x + (y - a.y) * (b.x - a.x) / (b.y - a.y));
            }
        cuts.sort((a, b) => a - b);
        for (let i = 1; i < cuts.length; i += 2)
            consider({ x: (cuts[i - 1] + cuts[i]) / 2, y });
    }
    const stepX = (maxX - minX) / 32, stepY = (maxY - minY) / 32;
    for (let y = 0; y < 32; y++)
        for (let x = 0; x < 32; x++)
            consider({ x: minX + (x + .5) * stepX, y: minY + (y + .5) * stepY });
    let dx = stepX, dy = stepY;
    for (let iteration = 0; iteration < 8; iteration++) {
        const seed = best;
        if (!seed)
            break;
        for (let y = -1; y <= 1; y++)
            for (let x = -1; x <= 1; x++)
                consider({ x: seed.point.x + x * dx, y: seed.point.y + y * dy });
        dx /= 2;
        dy /= 2;
    }
    return best;
}
//# sourceMappingURL=room-rings.js.map